import React, {useCallback, useState} from 'react';
import {UseFormSetError} from 'react-hook-form';
import {getErrorMessage, isActiveFormField} from '@chekin/core';
import {EXTRA_FORM_NAMES, FormTypes} from '../../utils/guestForm';
import {AddFormType} from '../../context/guest';
import {FieldsErrorResponse} from '@chekin/api';
import {GuestFormErrorModal} from '../GuestFormErrorModal';

const ExtraFieldNames = Object.values<string>(EXTRA_FORM_NAMES);

type UseErrorHandlerProps = {
  setError: UseFormSetError<FormTypes>;
  onDataIncomplete: () => void;
  fields?: AddFormType;
};
export function useErrorHandler({
  setError,
  fields,
  onDataIncomplete,
}: UseErrorHandlerProps) {
  const [fieldErrors, setFieldErrors] = useState<string | null>(null);

  const GuestErrorModal = useCallback(
    () => (
      <GuestFormErrorModal
        isOpen={Boolean(fieldErrors)}
        onClose={() => setFieldErrors(null)}
        fieldErrors={fieldErrors}
      />
    ),
    [fieldErrors],
  );

  const displayFieldErrors = useCallback(
    (error: FieldsErrorResponse) => {
      if (error?.errors?.length) {
        let isThereFieldError = false;
        error.errors
          .filter(
            error =>
              isActiveFormField(fields?.[error.field]) &&
              error.field !== 'non_field_errors' &&
              !ExtraFieldNames.includes(error.field),
          )
          .forEach(error => {
            const fieldError = error.field;
            const messageError = error.message;
            isThereFieldError = true;
            setError(
              fieldError,
              {type: 'custom', message: messageError},
              {shouldFocus: true},
            );
          });

        const nonFieldsErrorsMessages = error.errors.filter(
          error =>
            !isActiveFormField(fields?.[error.field]) ||
            error.field === 'non_field_errors',
        );

        if (nonFieldsErrorsMessages.length) {
          const errorMessage = getErrorMessage({
            errors: nonFieldsErrorsMessages,
          });

          setFieldErrors(errorMessage);
          return;
        }

        const hasExtraFieldsError = error.errors.some(error =>
          ExtraFieldNames.includes(error.field),
        );

        if (isThereFieldError || hasExtraFieldsError) {
          onDataIncomplete();
          return;
        }
      }
    },
    [fields, setError, onDataIncomplete],
  );

  return {GuestErrorModal, displayFieldErrors};
}
